import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const AccountStatementGenerator = ({
  customerData,
  credits,
  debits,
  companyDetails,
  prepaidBalance
}) => {
  const [facilityLogo, setFacilityLogo] = useState("");
  const location = useLocation();
  const [showDialog, setShowDialog] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showStatement, setShowStatement] = useState(false);
  const [filteredData, setFilteredData] = useState({ credits: [], debits: [] });
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const toast = useRef(null);

  useEffect(() => {
    getFacilityLogo();
  }, []);

  const getFacilityLogo = async () => {
    try {
      const facilityFromState = location.state?.facility;
      if (facilityFromState?.logo) {
        const fullLogoUrl = `${process.env.REACT_APP_BASE_URL}/${facilityFromState.logo}`;
        localStorage.setItem("selectedFacilityLogo", fullLogoUrl);
        setFacilityLogo(fullLogoUrl);
      } else {
        const storedLogo = localStorage.getItem("selectedFacilityLogo");
        if (storedLogo) {
          setFacilityLogo(storedLogo);
        } else {
          setFacilityLogo("");
        }
      }
    } catch (error) {
      console.error("Error fetching facility logo:", error);
      setFacilityLogo("");
    }
  };

  // ===== MASKING HELPERS =====
  const maskName = (fullName) => {
    if (!fullName || fullName === 'N/A') return fullName;
    const parts = fullName.trim().split(' ');
    return parts
      .map((part, index) => {
        if (index === 0) return part; // Keep first name fully visible
        if (part.length <= 1) return part;
        return part[0] + '*'.repeat(part.length - 1); // e.g. "Doe" → "D**"
      })
      .join(' ');
  };

  const maskPhone = (phone) => {
    if (!phone || phone === 'N/A') return phone;
    const cleaned = phone.replace(/\s+/g, '');
    if (cleaned.length < 7) return phone;
    // Show first 4 digits and last 3 digits, mask the middle
    // e.g. 0712345678 → 0712***678
    const visibleStart = cleaned.slice(0, 4);
    const visibleEnd = cleaned.slice(-3);
    const maskedMiddle = '*'.repeat(cleaned.length - 7);
    return `${visibleStart}${maskedMiddle}${visibleEnd}`;
  };

  const handleGenerateStatement = () => {
    if (!startDate || !endDate) {
      toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Please select both start and end dates' });
      return;
    }
    if (startDate > endDate) {
      toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Start date cannot be later than end date' });
      return;
    }

    const startOfDay = new Date(startDate);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(endDate);
    endOfDay.setHours(23, 59, 59, 999);

    const filteredCredits = credits.filter(credit => {
      if (credit.type !== 'Mobile Money') return false;
      let creditDate;
      try {
        creditDate = new Date(credit.addedOn);
        if (isNaN(creditDate.getTime())) {
          const parts = credit.addedOn.split('/');
          if (parts.length === 3) {
            creditDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
            if (isNaN(creditDate.getTime())) {
              creditDate = new Date(`${parts[2]}-${parts[0]}-${parts[1]}`);
            }
          }
        }
      } catch (error) {
        return false;
      }
      return !isNaN(creditDate.getTime()) && creditDate >= startOfDay && creditDate <= endOfDay;
    });

    const filteredDebits = debits.filter(debit => {
      let debitDate;
      try {
        debitDate = new Date(debit.date);
        if (isNaN(debitDate.getTime())) {
          const parts = debit.date.split('/');
          if (parts.length === 3) {
            debitDate = new Date(`${parts[2]}-${parts[0]}-${parts[1]}`);
            if (isNaN(debitDate.getTime())) {
              debitDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
            }
          }
        }
      } catch (error) {
        return false;
      }
      return !isNaN(debitDate.getTime()) && debitDate >= startOfDay && debitDate <= endOfDay;
    });

    setFilteredData({ credits: filteredCredits, debits: filteredDebits });
    setShowStatement(true);
    setShowDialog(false);
  };

  const handleDownloadPDF = async () => {
    const element = document.getElementById('account-statement-content');
    if (!element) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Statement content not found' });
      return;
    }

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `Water_Meter_Statement_${customerData?.accountNumber || 'unknown'}_${timestamp}.pdf`;

    try {
      setGeneratingPDF(true);
      toast.current?.show({ severity: 'info', summary: 'Processing', detail: 'Generating PDF...' });

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        allowTaint: true,
        height: element.scrollHeight,
        width: element.scrollWidth
      });

      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight, '', 'FAST');

      let heightLeft = imgHeight - pageHeight;
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight, '', 'FAST');
        heightLeft -= pageHeight;
      }

      pdf.save(filename);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Statement downloaded successfully!' });
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to generate PDF. Please try again.' });
    } finally {
      setGeneratingPDF(false);
    }
  };

  const formatCurrency = (amount) => `KES ${Number(amount || 0).toFixed(2)}`;

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try { return new Date(date).toLocaleDateString(); } catch { return 'Invalid Date'; }
  };

  const formatConsumption = (value) => {
    const num = parseFloat(value || 0);
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  const calculateSummary = () => {
    const totalCredits = filteredData.credits.reduce((sum, credit) => sum + (credit.amount || 0), 0);
    return { totalCredits, currentBalance: prepaidBalance };
  };

  const summary = calculateSummary();

  const renderDateRangeDialog = () => (
    <Dialog
      header="Generate Customer Water Meter Statement"
      visible={showDialog}
      style={{ width: '500px' }}
      onHide={() => setShowDialog(false)}
      modal
      className="p-fluid"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
          <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setShowDialog(false)} />
          <Button label="Generate Statement" icon="pi pi-file-pdf" className="p-button-primary" onClick={handleGenerateStatement} />
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <div className="field">
          <label htmlFor="startDate" style={{ fontWeight: 'bold', color: '#333' }}>
            Start Date <span style={{ color: 'red' }}>*</span>
          </label>
          <Calendar id="startDate" value={startDate} onChange={(e) => setStartDate(e.value)}
            dateFormat="dd/mm/yy" placeholder="Select start date" showIcon maxDate={new Date()} style={{ width: '100%' }} />
        </div>
        <div className="field">
          <label htmlFor="endDate" style={{ fontWeight: 'bold', color: '#333' }}>
            End Date <span style={{ color: 'red' }}>*</span>
          </label>
          <Calendar id="endDate" value={endDate} onChange={(e) => setEndDate(e.value)}
            dateFormat="dd/mm/yy" placeholder="Select end date" showIcon maxDate={new Date()} minDate={startDate} style={{ width: '100%' }} />
        </div>
        <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
          <small style={{ color: '#6c757d', lineHeight: '1.5' }}>
            <strong>Note:</strong> Choose the date range for your customer water meter statement.
            The statement will include Mobile Money payments and water consumption for the selected period.
          </small>
        </div>
        {/* Privacy notice in dialog */}
        <div style={{ padding: '0.75rem 1rem', backgroundColor: '#fff8e1', borderRadius: '8px', border: '1px solid #ffc107', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
          <i className="pi pi-shield" style={{ color: '#f59e0b', marginTop: '2px' }}></i>
          <small style={{ color: '#92400e', lineHeight: '1.5' }}>
            <strong>Privacy Notice:</strong> Customer name and phone number will be partially masked in the generated statement (e.g. <em>John D**</em>, <em>0712***678</em>).
          </small>
        </div>
      </div>
    </Dialog>
  );

  const renderAccountStatement = () => (
    <Dialog
      header="Customer Water Meter Statement"
      visible={showStatement}
      style={{ width: '90vw', height: '90vh' }}
      onHide={() => setShowStatement(false)}
      modal
      maximizable
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
          <Button label="Close" icon="pi pi-times" className="p-button-text" onClick={() => setShowStatement(false)} />
          <Button label="Download PDF" icon="pi pi-download" className="p-button-primary" onClick={handleDownloadPDF} loading={generatingPDF} />
        </div>
      }
    >
      <div id="account-statement-content" style={statementStyles.container}>
        <div style={statementStyles.content}>

          {/* Header */}
          <div style={statementStyles.header}>
            <div style={statementStyles.headerContent}>
              {facilityLogo && (
                <div style={statementStyles.logoContainer}>
                  <img src={facilityLogo} alt="Company Logo" style={statementStyles.logo} />
                </div>
              )}
              <h1 style={statementStyles.title}>CUSTOMER WATER METER STATEMENT</h1>
            </div>
          </div>

          {/* Body */}
          <div style={statementStyles.body}>

            {/* Customer Info */}
            <div style={statementStyles.customerSection}>
              <div style={statementStyles.customerGrid}>
                <div>
                  <h2 style={statementStyles.sectionTitle}>SERVICE PROVIDER</h2>
                  <div style={statementStyles.addressContent}>
                    <div><strong>{companyDetails?.name || 'PayServ'}</strong></div>
                    <div>{companyDetails?.address || 'Company Address'}</div>
                    <div>{companyDetails?.country || 'Kenya'}</div>
                    <div>Phone: {companyDetails?.phone || 'N/A'}</div>
                    <div>Email: {companyDetails?.email || 'N/A'}</div>
                  </div>
                </div>
                <div>
                  <h2 style={statementStyles.sectionTitle}>CUSTOMER DETAILS</h2>
                  <div style={statementStyles.addressContent}>
                    {/* MASKED: name and phone */}
                    <div><strong>{maskName(customerData?.name) || 'N/A'}</strong></div>
                    <div>Account: {customerData?.accountNumber || 'N/A'}</div>
                    <div>Meter: {customerData?.meterNumber || 'N/A'}</div>
                    <div>Phone: {maskPhone(customerData?.phone) || 'N/A'}</div>
                    <div>Status: {customerData?.status || 'N/A'}</div>
                  </div>
                </div>
                <div style={statementStyles.balanceSection}>
                  <h2 style={statementStyles.sectionTitle}>CURRENT BALANCE</h2>
                  <div style={statementStyles.balanceAmount}>{formatCurrency(summary.currentBalance)}</div>
                </div>
              </div>
            </div>

            {/* Statement Period Bar */}
            <div style={statementStyles.statementInfoBar}>
              <div style={statementStyles.infoItem}>
                <span style={statementStyles.infoLabel}>Statement Period:</span>
                <span style={statementStyles.infoValue}>{formatDate(startDate)} - {formatDate(endDate)}</span>
              </div>
              <div style={statementStyles.infoItem}>
                <span style={statementStyles.infoLabel}>Generated On:</span>
                <span style={statementStyles.infoValue}>{formatDate(new Date())}</span>
              </div>
            </div>

            {/* Privacy notice inside the statement document */}
            <div style={statementStyles.privacyNotice}>
              <span style={{ marginRight: '0.4rem' }}>🔒</span>
              <span>Customer name and phone number are partially masked for privacy protection.</span>
            </div>

            {/* Summary */}
            <div style={statementStyles.summarySection}>
              <h2 style={statementStyles.sectionHeading}>Statement Summary</h2>
              <div style={statementStyles.summaryGrid}>
                <div style={statementStyles.summaryCard}>
                  <div style={statementStyles.summaryLabel}>Total Mobile Money Payments</div>
                  <div style={{ ...statementStyles.summaryValue, color: '#28a745' }}>{formatCurrency(summary.totalCredits)}</div>
                </div>
                <div style={statementStyles.summaryCard}>
                  <div style={statementStyles.summaryLabel}>Current Balance</div>
                  <div style={statementStyles.summaryValue}>{formatCurrency(summary.currentBalance)}</div>
                </div>
              </div>
            </div>

            {/* PAYMENTS TABLE */}
            {filteredData.credits.length > 0 && (
              <div style={statementStyles.tableSection}>
                <h2 style={statementStyles.sectionHeading}>Mobile Money Payments</h2>
                <table style={statementStyles.table}>
                  <colgroup>
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '15%' }} />
                  </colgroup>
                  <thead>
                    <tr style={statementStyles.tableHeaderRow}>
                      <th style={statementStyles.th}>Date</th>
                      <th style={statementStyles.th}>Time</th>
                      <th style={statementStyles.th}>Reference</th>
                      <th style={statementStyles.th}>Amount</th>
                      <th style={{ ...statementStyles.th, borderRight: 'none' }}>Payment Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.credits.map((credit, index) => (
                      <tr key={credit._id || index} style={index % 2 === 0 ? statementStyles.trEven : statementStyles.trOdd}>
                        <td style={statementStyles.td}>{credit.addedOn}</td>
                        <td style={statementStyles.td}>{credit.time}</td>
                        <td style={statementStyles.td}>{credit.ref}</td>
                        <td style={{ ...statementStyles.td, color: '#28a745', fontWeight: '600' }}>{formatCurrency(credit.amount)}</td>
                        <td style={{ ...statementStyles.td, borderRight: 'none' }}>{credit.type}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* CONSUMPTION TABLE */}
            {filteredData.debits.length > 0 && (
              <div style={statementStyles.tableSection}>
                <h2 style={statementStyles.sectionHeading}>Water Consumption</h2>
                <table style={statementStyles.table}>
                  <colgroup>
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '22%' }} />
                    <col style={{ width: '20%' }} />
                  </colgroup>
                  <thead>
                    <tr style={statementStyles.tableHeaderRow}>
                      <th style={statementStyles.th}>Date</th>
                      <th style={statementStyles.th}>Previous Reading</th>
                      <th style={statementStyles.th}>Current Reading</th>
                      <th style={statementStyles.th}>Consumption</th>
                      <th style={{ ...statementStyles.th, borderRight: 'none' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.debits.map((debit, index) => (
                      <tr key={debit._id || index} style={index % 2 === 0 ? statementStyles.trEven : statementStyles.trOdd}>
                        <td style={statementStyles.td}>{debit.date}</td>
                        <td style={statementStyles.td}>{debit.prev_reading || 0}</td>
                        <td style={statementStyles.td}>{debit.current_reading || 0}</td>
                        <td style={statementStyles.td}>{formatConsumption(debit.consumption)} units</td>
                        <td style={{ ...statementStyles.td, color: '#dc3545', fontWeight: '600', borderRight: 'none' }}>
                          {formatCurrency(debit.totalAmount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Footer */}
            <div style={statementStyles.footer}>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontWeight: 'bold', margin: '0 0 10px 0' }}>This is a system generated statement</p>
                <p style={{ color: '#6c757d', margin: 0, fontSize: '0.875rem' }}>
                  Generated on {new Date().toLocaleString()} | For inquiries, please contact customer service
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </Dialog>
  );

  return (
    <>
      <Toast ref={toast} />
      <Button
        label="Generate Water Meter Statement"
        icon="pi pi-file-pdf"
        className="p-button-outlined"
        onClick={() => setShowDialog(true)}
      />
      {renderDateRangeDialog()}
      {renderAccountStatement()}
    </>
  );
};

// ── Styles ──────────────────────────────────────────────────────────────────
const statementStyles = {
  container: {
    backgroundColor: 'white',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
    borderRadius: '8px',
    overflow: 'hidden',
    width: '210mm',
    minHeight: '297mm',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    backgroundColor: 'white',
    position: 'relative',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    backgroundColor: '#2196f3',
    padding: '1.5rem 2rem',
    color: 'white',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '2rem',
  },
  title: {
    color: 'white',
    fontSize: '1.5rem',
    fontWeight: '600',
    margin: 0,
    textTransform: 'uppercase',
    flex: 1,
    textAlign: 'right',
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  logo: {
    maxWidth: '120px',
    maxHeight: '60px',
    objectFit: 'contain',
  },
  body: {
    padding: '1.5rem 2rem',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  customerSection: { marginBottom: '1.5rem' },
  customerGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '2rem',
    alignItems: 'start',
  },
  sectionTitle: {
    color: '#806c6b',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    marginBottom: '0.5rem',
    fontWeight: '600',
  },
  addressContent: {
    fontSize: '0.875rem',
    lineHeight: '1.4',
    color: '#111827',
  },
  balanceSection: { textAlign: 'right' },
  balanceAmount: {
    color: '#2196f3',
    fontSize: '1.5rem',
    fontWeight: '600',
    marginTop: '0.25rem',
  },
  statementInfoBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    backgroundColor: '#f0f8ff',
    borderRadius: '8px',
    marginBottom: '0.75rem',
    border: '1px solid #2196f3',
  },
  infoItem: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  infoLabel: { fontWeight: '600', color: '#2196f3', fontSize: '0.875rem' },
  infoValue: { color: '#1f2937', fontSize: '0.875rem' },

  // Privacy notice strip inside the printed statement
  privacyNotice: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.5rem 0.75rem',
    backgroundColor: '#fff8e1',
    border: '1px solid #ffc107',
    borderRadius: '6px',
    marginBottom: '1rem',
    fontSize: '0.78rem',
    color: '#92400e',
    fontStyle: 'italic',
  },

  summarySection: {
    marginBottom: '1.5rem',
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e9ecef',
  },
  sectionHeading: {
    color: '#2196f3',
    fontSize: '1rem',
    textTransform: 'uppercase',
    fontWeight: 'bold',
    marginBottom: '1rem',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '1rem',
  },
  summaryCard: {
    backgroundColor: 'white',
    padding: '1rem',
    borderRadius: '8px',
    textAlign: 'center',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  summaryLabel: { fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.5rem', fontWeight: '500' },
  summaryValue: { fontSize: '1.25rem', fontWeight: '600', color: '#2196f3' },
  tableSection: { marginBottom: '1.5rem' },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    backgroundColor: 'white',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    border: '1px solid #e5e7eb',
  },
  tableHeaderRow: {
    backgroundColor: '#2196f3',
  },
  th: {
    padding: '0.75rem 0.5rem',
    fontSize: '0.8rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    textAlign: 'center',
    color: 'white',
    borderRight: '1px solid rgba(255,255,255,0.25)',
    borderBottom: '2px solid rgba(255,255,255,0.3)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  trEven: { backgroundColor: '#ffffff' },
  trOdd:  { backgroundColor: '#f7fbff' },
  td: {
    padding: '0.7rem 0.5rem',
    fontSize: '0.855rem',
    textAlign: 'center',
    color: '#1f2937',
    borderRight: '1px solid #e5e7eb',
    borderBottom: '1px solid #e5e7eb',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  footer: {
    marginTop: 'auto',
    padding: '1.5rem 2rem',
    backgroundColor: '#f3f4f6',
    borderTop: '1px solid #e5e7eb',
  },
};

export default AccountStatementGenerator;